import { useRef } from "react"
import { useQuery, useQueryClient } from "react-query"
import _ from "lodash"
import {
  getEvent, getEvents,
  getLeadRetrievalLicenses,
  getLeadScans,
  getPublicEvent,
  getSegmentSizes,
  resolveLeadRetrievalLicenseToken,
  getEventActivity,
  getEventPageViews,
  putEvent,
} from "api/events"
import { matchPath, useLocation } from "react-router-dom"
import { isUUID } from "utilities/strings"
import { errorToast } from "utilities/toasts"
import { EventUserRole } from "services/permissions"
import { currentUserId } from "services/authentication"
import {
  DashboardLayout, DioboxEvent, EventActivityParams, EventPageViewsParams,
} from "../sharedTypes"

export const eventsCacheKey = (
  accountId: string, dateFilter: string|null, cloneId: string|null,
) => ["events", accountId, dateFilter, cloneId]
export const eventCacheKey = (eventId: string) => ["event", eventId]
const publicEventCacheKey = (eventId: string) => ["publicEventCacheKey", eventId]
export const leadRetrievalLicensesCacheKey = (eventId: string) => ["leadRetrievalLicenses", eventId]
export const leadScansCacheKey = (eventId: string, eventAccessId: number) => ["leadScans", eventId, eventAccessId]
export const segmentSizesCacheKey = (eventId: string) => ["segmentSizes", eventId]

export const useCurrentEvent = () => {
  const eventId = (matchPath(useLocation().pathname, "/events/:eventId")?.params as any)?.eventId

  const { isLoading, data: event } = useQuery(
    eventCacheKey(eventId), () => getEvent(eventId).then(({ data }) => {
      // eslint-disable-next-line no-param-reassign
      data.currentUserRole = (data.users || [])
        .find((eventUser) => eventUser.user?.id === currentUserId())?.role as EventUserRole

      return data
    }),
    { enabled: isUUID(eventId) },
  )

  return { isLoading, data: event || {} as DioboxEvent }
}

// Shows a hand tick at once and saves the event's ticks; if saving fails, only this step goes
// back to what it was, unless it was ticked again since. Saves run one at a time, each
// sending the latest ticks, and only the last reply updates the ticks shown.
export const useChecklistTick = (eventId: string) => {
  const queryClient = useQueryClient()
  const queue = useRef<Promise<unknown>>(Promise.resolve())
  const latest = useRef(0)
  const latestOfStep = useRef<Record<string, number>>({})

  return (step: string, done: boolean | null) => {
    const key = eventCacheKey(eventId)
    const ticks = () => queryClient.getQueryData<DioboxEvent>(key)?.checklistTicks ?? {}
    const setTicks = (checklistTicks: Record<string, boolean>) => queryClient.setQueryData(
      key, (event: DioboxEvent | undefined) => ({ ...event, checklistTicks }) as DioboxEvent,
    )
    const setStep = (value?: boolean | null) => setTicks(
      value === null || value === undefined ? _.omit(ticks(), step) : { ...ticks(), [step]: value },
    )
    const previous = ticks()[step]
    latest.current += 1
    const save = latest.current
    latestOfStep.current[step] = save

    setStep(done)

    const saving = queue.current
      .then(() => putEvent(eventId, { checklistTicks: ticks() } as DioboxEvent))
      .then(({ data }) => {
        if (save === latest.current) setTicks(data.checklistTicks ?? {})
      })
      .catch(() => {
        if (latestOfStep.current[step] === save) setStep(previous)
      })
    queue.current = saving

    return saving
  }
}

// Shows the dashboard layout at once and saves it on the event; null goes back to the default.
// Saves run one at a time and only the last one's reply is shown. If the last save fails,
// the last layout the server saved comes back.
export const useSaveDashboardLayout = (eventId: string) => {
  const queryClient = useQueryClient()
  const queue = useRef<Promise<unknown>>(Promise.resolve())
  const latest = useRef(0)
  const confirmed = useRef<DioboxEvent["dashboardLayout"]>()

  return (layout: DashboardLayout | null) => {
    const key = eventCacheKey(eventId)
    const setLayout = (dashboardLayout: DioboxEvent["dashboardLayout"]) => (
      queryClient.setQueryData(
        key, (event: DioboxEvent | undefined) => ({ ...event, dashboardLayout }) as DioboxEvent,
      )
    )
    const dashboardLayout = layout ?? {}
    if (latest.current === 0) {
      confirmed.current = queryClient.getQueryData<DioboxEvent>(key)?.dashboardLayout ?? {}
    }
    latest.current += 1
    const save = latest.current

    setLayout(dashboardLayout)

    const saving = queue.current
      .then(() => putEvent(eventId, { dashboardLayout } as DioboxEvent))
      .then(({ data }) => {
        if (!data.dashboardLayout) throw new Error("Layout not saved")
        confirmed.current = data.dashboardLayout
        if (save === latest.current) setLayout(data.dashboardLayout)
      })
      .catch(() => {
        if (save !== latest.current) return
        setLayout(confirmed.current)
        errorToast({
          title: "Your dashboard layout couldn't be saved.",
          description: "Please try again.",
        })
      })
    queue.current = saving

    return saving
  }
}

export const useEvents = (
  accountId: string, dateFilter?: string|null, cloneId?: string|null,
) => useQuery(
  eventsCacheKey(accountId, dateFilter || null, cloneId || null),
  () => getEvents(dateFilter || null).then(({ data }) => data),
)

export const usePublicEvent = (eventId: string) => useQuery(publicEventCacheKey(eventId),
  () => getPublicEvent(eventId).then(({ data }) => data))

export const useSegmentSizes = (eventId: string) => {
  const { data } = useQuery(
    segmentSizesCacheKey(eventId),
    () => getSegmentSizes(eventId).then(({ data: sizes }) => sizes),
    { enabled: !!eventId },
  )

  return data
}

export const useLeadRetrievalLicenses = (eventId: string) => useQuery(
  leadRetrievalLicensesCacheKey(eventId),
  () => getLeadRetrievalLicenses(eventId).then(({ data }) => data),
)

export const useLeadScans = (eventId: string, eventAccessId?: number) => useQuery(
  leadScansCacheKey(eventId, eventAccessId ?? -1),
  () => getLeadScans(eventId, eventAccessId as number).then(({ data }) => data),
  { enabled: eventAccessId !== undefined },
)

export const leadRetrievalLicenseTokenCacheKey = (token: string) => ["leadRetrievalLicenseToken", token]

export const useLeadRetrievalLicenseToken = (token: string) => useQuery(
  leadRetrievalLicenseTokenCacheKey(token),
  () => resolveLeadRetrievalLicenseToken(token).then(({ data }) => data),
)

export const eventActivityCacheKey = (eventId: string, params: EventActivityParams) => (
  ["eventActivity", eventId, params]
)

export const useEventActivity = (
  eventId: string,
  params: EventActivityParams,
) => useQuery(
  eventActivityCacheKey(eventId, params),
  () => getEventActivity(eventId, params).then(({ data }) => data),
)

export const eventPageViewsCacheKey = (eventId: string, params: EventPageViewsParams) => (
  ["eventPageViews", eventId, params]
)

export const useEventPageViews = (eventId: string, params: EventPageViewsParams) => useQuery(
  eventPageViewsCacheKey(eventId, params),
  () => getEventPageViews(eventId, params).then(({ data }) => data),
)
